import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import '../styles/registeruser.css';
import { BASE_URL } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCheckCircle, faExclamationTriangle, faLock, faPhone, faShieldAlt } from "@fortawesome/free-solid-svg-icons";

// ===== دوال الحماية من XSS لإعادة تعيين كلمة المرور =====

// تنظيف المدخلات
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/[<>'"&]/g, (match) => {
      const entityMap = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '&': '&amp;'
      };
      return entityMap[match];
    });
};

// فحص أكواد XSS
const detectXSS = (input) => {
  if (typeof input !== 'string') return false;
  
  const xssPatterns = [
    /alert\s*\(/i,
    /confirm\s*\(/i,
    /prompt\s*\(/i,
    /document\./i,
    /window\./i,
    /eval\s*\(/i,
    /<script/i,
    /<iframe/i,
    /<object/i,
    /javascript:/i,
    /vbscript:/i,
    /on\w+\s*=/
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
};

// التحقق من صحة البيانات
const validateDataType = (input, fieldName) => {
  if (typeof input !== 'string') return false;
  
  const maxLengths = { phone: 15, password: 100, code: 10 };
  if (maxLengths[fieldName] && input.length > maxLengths[fieldName]) return false;
  
  const suspiciousPatterns = [
    /<[^>]*>/,
    /javascript:/i,
    /union\s+select/i,
    /drop\s+table/i,
    /\.\./
  ];
  
  return !suspiciousPatterns.some(pattern => pattern.test(input));
};

// دالة التنظيف الشاملة
const secureResetInput = (input, fieldName) => {
  if (!input || typeof input !== 'string') return input;
  
  if (detectXSS(input)) {
    console.warn(`XSS attempt detected in reset field: ${fieldName}`);
    return '';
  }
  
  if (!validateDataType(input, fieldName)) {
    console.warn(`Invalid data type in reset field: ${fieldName}`);
    return '';
  }
  
  return sanitizeInput(input.trim());
};

// Validation functions
const validateField = (name, value) => {
  const errors = {};

  switch (name) {
    case 'phone':
      if (!value.trim()) {
        errors[name] = 'رقم الهاتف مطلوب';
      } else if (!/^01[0125][0-9]{8}$/.test(value.trim())) {
        errors[name] = 'يجب إدخال رقم هاتف مصري صحيح';
      }
      break;

    case 'code':
      if (!value.trim()) {
        errors[name] = 'كود التحقق مطلوب';
      } else if (!/^[0-9]{6}$/.test(value.trim())) {
        errors[name] = 'كود التحقق يجب أن يكون 6 أرقام';
      }
      break;

    case 'password':
      if (!value) {
        errors[name] = 'كلمة المرور مطلوبة';
      } else if (value.length < 6) {
        errors[name] = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
      }
      break;

    case 'password_confirmation':
      if (!value) {
        errors[name] = 'تأكيد كلمة المرور مطلوب';
      }
      break;

    default:
      break;
  }

  return errors;
};

const validateForm = (formData) => {
  const errors = {};
  
  Object.keys(formData).forEach(field => {
    const fieldErrors = validateField(field, formData[field]);
    if (fieldErrors[field]) {
      errors[field] = fieldErrors[field];
    }
  });

  // تأكيد كلمة المرور
  if (formData.password && formData.password_confirmation && 
      formData.password !== formData.password_confirmation) {
    errors.password_confirmation = 'كلمة المرور غير متطابقة';
  }

  return errors;
};

export const ResetPassword = ({ style }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phone: "",
    code: "",
    password: "",
    password_confirmation: ""
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: إرسال الكود, 2: إعادة تعيين كلمة المرور
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    // تطبيق الحماية من XSS على المدخلات
    const securedValue = secureResetInput(value, name);
    
    // إذا تم حذف المحتوى بسبب XSS، أظهر تحذير
    if (value && !securedValue && value.trim() !== '') {
      swal("تحذير أمني!", "تم رفض المدخلات لاحتوائها على محتوى غير آمن", "warning");
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: securedValue }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validate field on blur
    const fieldErrors = validateField(name, value);
    if (fieldErrors[name]) {
      setErrors(prev => ({ ...prev, [name]: fieldErrors[name] }));
    }
  };

  const handleSendCode = async (event) => {
    event.preventDefault();
    
    // فحص أمني شامل قبل الإرسال
    const securityCheck = () => {
      let hasSecurityIssue = false;
      const value = formData.phone;
      if (value && typeof value === 'string') {
        if (detectXSS(value)) {
          console.error(`XSS detected in phone:`, value);
          hasSecurityIssue = true;
        }
        if (!validateDataType(value, 'phone')) {
          console.error(`Invalid data type in phone:`, value);
          hasSecurityIssue = true;
        }
      }
      return !hasSecurityIssue;
    };
    
    if (!securityCheck()) {
      swal({
        title: "خطأ أمني!",
        text: "تم اكتشاف محتوى غير آمن في البيانات.",
        icon: "error",
        button: "حسناً",
      });
      return;
    }
    
    // Validate phone field
    const phoneErrors = validateField('phone', formData.phone);
    if (phoneErrors.phone) {
      setErrors(phoneErrors);
      setTouched({ phone: true });
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      const sanitizedData = {
        phone: secureResetInput(formData.phone, 'phone')
      };
      
      const response = await axios.post(`${BASE_URL}/api/send-reset-code`, sanitizedData);
      
      setOtpSent(true);
      setStep(2);
      setCountdown(240); // 4 minutes
      
      // Start countdown
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      swal({
        title: "تم إرسال الكود! 📱",
        text: `تم إرسال كود التحقق إلى رقم ${formData.phone} عبر الواتساب`,
        icon: "success",
        button: "حسناً",
      });
    } catch (e) {
      if (e.response) {
        if (e.response.status === 404) {
          swal({ 
            title: "رقم الهاتف غير مسجل", 
            text: "يرجى التحقق من رقم الهاتف أو إنشاء حساب جديد", 
            icon: "error", 
            button: "حسناً" 
          });
        } else if (e.response.status === 422) {
          setErrors(e.response.data.errors);
          setTouched({ phone: true });
        }
      } else {
        swal({
          title: "خطأ في الشبكة!",
          text: "يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.",
          icon: "error",
          button: "حسناً",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    
    // فحص أمني شامل قبل الإرسال
    const securityCheck = () => {
      let hasSecurityIssue = false;
      const textFields = ['phone', 'code', 'password', 'password_confirmation'];
      
      textFields.forEach(field => {
        const value = formData[field];
        if (value && typeof value === 'string') {
          if (detectXSS(value)) {
            console.error(`XSS detected in reset ${field}:`, value);
            hasSecurityIssue = true;
          }
          if (!validateDataType(value, field)) {
            console.error(`Invalid data type in reset ${field}:`, value);
            hasSecurityIssue = true;
          }
        }
      });
      
      return !hasSecurityIssue;
    };
    
    if (!securityCheck()) {
      swal({
        title: "خطأ أمني!",
        text: "تم اكتشاف محتوى غير آمن في بيانات إعادة تعيين كلمة المرور.",
        icon: "error",
        button: "حسناً",
      });
      return;
    }
    
    // Validate form
    const formErrors = validateForm(formData);
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      
      swal({
        title: "خطأ في البيانات!",
        text: "يرجى مراجعة البيانات المدخلة وإصلاح الأخطاء المذكورة.",
        icon: "error",
        button: "حسناً",
      });
      
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      // تنظيف البيانات مرة أخيرة قبل الإرسال
      const sanitizedData = {
        phone: secureResetInput(formData.phone, 'phone'),
        code: secureResetInput(formData.code, 'code'),
        password: secureResetInput(formData.password, 'password'),
        password_confirmation: secureResetInput(formData.password_confirmation, 'password_confirmation')
      };
      
      const response = await axios.post(`${BASE_URL}/api/reset-password-with-code`, sanitizedData);
      const token = response.data.data.token;
      
      localStorage.setItem("token", token);
      localStorage.setItem("sha1", response.data.data.user.role);

      swal({
        title: "تم إعادة تعيين كلمة المرور بنجاح! 🎉",
        text: "يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة",
        icon: "success",
        button: "تسجيل الدخول",
      }).then(() => {
        navigate("/loginuser");
      });
    } catch (e) {
      if (e.response) {
        if (e.response.status === 400) {
          if (e.response.data.message.includes('غير صحيح')) {
            swal({ 
              title: "كود التحقق غير صحيح", 
              text: "يرجى التحقق من الكود المدخل", 
              icon: "error", 
              button: "حسناً" 
            });
          } else if (e.response.data.message.includes('منتهي الصلاحية')) {
            swal({ 
              title: "كود التحقق منتهي الصلاحية", 
              text: "يرجى طلب كود جديد", 
              icon: "warning", 
              button: "حسناً" 
            });
            setStep(1);
            setOtpSent(false);
            setCountdown(0);
          }
        } else if (e.response.status === 422) {
          setErrors(e.response.data.errors);
          setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
        }
      } else {
        swal({
          title: "خطأ في الشبكة!",
          text: "يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.",
          icon: "error",
          button: "حسناً",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getFieldStatus = (fieldName) => {
    if (touched[fieldName] && errors[fieldName]) {
      return 'error';
    }
    if (touched[fieldName] && formData[fieldName] && !errors[fieldName]) {
      return 'success';
    }
    return 'default';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={`stylish-register-page ${style ? 'dark-mode' : ''}`}>
      <div className="form-box">
        <div className="form-panel">
          <form onSubmit={step === 1 ? handleSendCode : handleResetPassword} noValidate>
            <div className="title-wrapper">
              <h2>
                {step === 1 ? 'إعادة تعيين' : 'كلمة المرور الجديدة'}
                <span> كلمة المرور</span>
              </h2>
              {step === 1 && (
                <p className="step-description">
                  أدخل رقم هاتفك لإرسال كود التحقق عبر الواتساب
                </p>
              )}
              {step === 2 && (
                <p className="step-description">
                  أدخل كود التحقق وكلمة المرور الجديدة
                </p>
              )}
            </div>

            {step === 1 ? (
              <>
                <div className={`input-field ${getFieldStatus('phone')}`}>
                  <input 
                    id="phone" 
                    type="number" 
                    placeholder=" " 
                    value={formData.phone} 
                    name="phone"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required 
                  />
                  <label htmlFor="phone">
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    رقم الهاتف
                  </label>
                  {getFieldStatus('phone') === 'success' && (
                    <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                  )}
                  {getFieldStatus('phone') === 'error' && (
                    <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                  )}
                  {errors.phone && <small className="error-text">{errors.phone}</small>}
                </div>
              </>
            ) : (
              <>
                <div className={`input-field ${getFieldStatus('phone')}`}>
                  <input 
                    id="phone" 
                    type="number" 
                    placeholder=" " 
                    value={formData.phone} 
                    name="phone"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required 
                    disabled
                  />
                  <label htmlFor="phone">
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    رقم الهاتف
                  </label>
                </div>

                <div className={`input-field ${getFieldStatus('code')}`}>
                  <input 
                    id="code" 
                    type="text" 
                    placeholder=" " 
                    value={formData.code} 
                    name="code"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required 
                    maxLength="6"
                  />
                  <label htmlFor="code">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    كود التحقق
                  </label>
                  {getFieldStatus('code') === 'success' && (
                    <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                  )}
                  {getFieldStatus('code') === 'error' && (
                    <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                  )}
                  {errors.code && <small className="error-text">{errors.code}</small>}
                </div>

                <div className={`input-field password-field ${getFieldStatus('password')}`}>
                  <input 
                    id="password" 
                    type={showPassword ? 'text' : 'password'} 
                    placeholder=" " 
                    value={formData.password} 
                    name="password"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required 
                  />
                  <label htmlFor="password">
                    <FontAwesomeIcon icon={faLock} className="me-2" />
                    كلمة المرور الجديدة
                  </label>
                  <span className="password-toggle-icon" onClick={() => setShowPassword(!showPassword)}>
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </span>
                  {getFieldStatus('password') === 'success' && (
                    <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                  )}
                  {getFieldStatus('password') === 'error' && (
                    <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                  )}
                  {errors.password && <small className="error-text">{errors.password}</small>}
                </div>

                <div className={`input-field password-field ${getFieldStatus('password_confirmation')}`}>
                  <input 
                    id="password_confirmation" 
                    type={showConfirmPassword ? 'text' : 'password'} 
                    placeholder=" " 
                    value={formData.password_confirmation} 
                    name="password_confirmation"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required 
                  />
                  <label htmlFor="password_confirmation">
                    <FontAwesomeIcon icon={faLock} className="me-2" />
                    تأكيد كلمة المرور
                  </label>
                  <span className="password-toggle-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </span>
                  {getFieldStatus('password_confirmation') === 'success' && (
                    <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                  )}
                  {getFieldStatus('password_confirmation') === 'error' && (
                    <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                  )}
                  {errors.password_confirmation && <small className="error-text">{errors.password_confirmation}</small>}
                </div>

                {countdown > 0 && (
                  <div className="countdown-timer">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    كود التحقق صالح لمدة: {formatTime(countdown)}
                  </div>
                )}
              </>
            )}
            
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  <span>
                    {step === 1 ? 'جاري الإرسال...' : 'جاري إعادة التعيين...'}
                  </span>
                </>
              ) : (
                step === 1 ? 'إرسال كود التحقق' : 'إعادة تعيين كلمة المرور'
              )}
            </button>

            {step === 2 && (
              <div className="step-actions">
                <button 
                  type="button" 
                  className="back-btn"
                  onClick={() => {
                    setStep(1);
                    setOtpSent(false);
                    setCountdown(0);
                    setFormData(prev => ({ ...prev, code: '', password: '', password_confirmation: '' }));
                    setErrors({});
                    setTouched({});
                  }}
                >
                  العودة لإرسال كود جديد
                </button>
              </div>
            )}
          </form>
        </div>
        
        <div className="welcome-panel">
          <div className="welcome-content">
            <h2>نسيت كلمة المرور؟</h2>
            <p>لا تقلق! يمكنك إعادة تعيين كلمة المرور بسهولة باستخدام رقم هاتفك المسجل.</p>
            <div className="reset-steps">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-text">أدخل رقم هاتفك</div>
              </div>
              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-text">احصل على كود التحقق</div>
              </div>
              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-text">أدخل كلمة المرور الجديدة</div>
              </div>
            </div>
            <Link to="/loginuser" className="welcome-link">تذكرت كلمة المرور؟ سجل دخولك</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
